import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import Campus from "../../types/core/Campus";

// Define the type for our state store entry
type CampusState = {
    campuses: Campus[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: CampusState = {
    campuses: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const campusSlice = createSlice({
    name: "campus",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    campuses:
                        rawData?.payload?.campus?.campuses?.map(
                            c => new Campus(c),
                        ) || [],
                    status: !rawData?.payload?.campus?.campuses?.length
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchCampuses.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchCampuses.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.campuses = action.payload.map(c => new Campus(c));
            })
            .addCase(fetchCampuses.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addCampus.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addCampus.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.campuses = state.campuses.concat(
                    action.payload.map(c => new Campus(c)),
                );
            })
            .addCase(addCampus.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateCampus.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateCampus.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const campusIds = action.payload.map(c => c.id);
                state.campuses = state.campuses
                    .filter(c => !campusIds.includes(c.id))
                    .concat(action.payload.map(c => new Campus(c)));
            })
            .addCase(updateCampus.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const {} = campusSlice.actions;

export const initialiseCampusDetails = createAsyncThunk(
    "campus/initialiseCampusDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchCampuses()),
);

// This is a thunk, they are used to load the state
export const fetchCampuses = createAsyncThunk(
    "campus/fetchCampuses",
    () => Datastore.getType<Campus[]>("api/Campuses"),
    {
        condition: (_, api) =>
            store.getState().db.campus.status === "idle",
    },
);
export const addCampus = createAsyncThunk(
    "campus/addCampus",
    (payload: Campus[]) =>
        Datastore.postType<Campus[]>("api/Campuses", payload),
    {
        condition: (_, api) =>
            store.getState().db.campus.status === "succeeded",
    },
);
export const updateCampus = createAsyncThunk(
    "campus/updateCampus",
    (payload: Campus[]) => Datastore.putType<Campus[]>("api/Campuses", payload),
    {
        condition: (_, api) =>
            (store.getState().db.campus.status === "succeeded"),
    },
);

// We have to export the reducer as this is added to the state store
export default campusSlice.reducer;
